import type { IdentityV1 } from '@alpod-ai-writer/backend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Label, TextInput } from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router';
import 'react-phone-number-input/style.css';
import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { Loading } from '../../../components/loading/loading.component';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { ToastNotificationType } from '../../components/toastNotification/toastNotifications.context';
import { useToastNotifications } from '../../components/toastNotification/useToastNotifications.hook';
import { RouteIndex } from '../../routeIndex';

interface FormValues {
    name: string;
    phone: string;
}


const PersonalDetailsView = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const toastNotifications = useToastNotifications();
    const { data: identity, error, isLoading } = useQuery({
        queryKey: ['my-identity'],
        queryFn: async () => {
            return api.v1.identities.getMyIdentity();
        }
    });

    const saveUserDetails = useMutation({
        mutationFn: (update: IdentityV1.OwnUpdate) => {
            return api.v1.identities.updateMyIdentity(update);
        },
        onSuccess: () => {
            toastNotifications.showToast({ type: ToastNotificationType.SUCCESS, message: 'Detaily byly uloženy' });
            queryClient.invalidateQueries({ queryKey: ['my-identity'] });
            navigate(RouteIndex.documentList);
        }
    });

    const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<FormValues>({
        defaultValues: { name: '', phone: '' }
    });

    useEffect(() => {
        if (!identity) return;
        const googleName = identity.name !== identity.email ? identity.name : '';
        reset({
            name: (identity.userFields['name'] as string) || googleName || '',
            phone: ((identity.userFields['phone'] as string) ?? '').replace(/\s/g, ''),
        });
    }, [identity, reset]);

    const onSubmit = useCallback((formValues: FormValues) => {
        if (isLoading || !identity) {
            return;
        }

        const update = {
            ...identity,
            name: formValues.name,
            userFields: {
                ...identity.userFields,
                name: formValues.name,
                phone: formValues.phone,
                billingName: identity.userFields['billingName'] ?? formValues.name,
                onboardingCompleted: true,
            }
        };

        saveUserDetails.mutate(update);
    }, [ identity, isLoading ]);

    // Extract phone from form to set value to the phone input, since "register" doesn't work properly
    const phone = watch('phone');

    return (
        <>
            <AppHeader
                pageTitle='Dokončení registrace'
            >
            </AppHeader>

            <div className="flex flex-col justify-start items-center min-h-screen pt-14">
                {!identity && isLoading && (
                    <div className='flex flex-row items-center justify-center min-h-screen'>
                        <Loading label='Načítání dat...' />
                    </div>
                )}

                <ErrorSwitch
                    error={error}
                    default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                    options={[]}
                    retry={() => queryClient.invalidateQueries({ queryKey: ['my-identity'] })}
                />

                {identity && (
                    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6 p-8 lg:pb-24 w-[36rem] max-w-full grow justify-center'>
                        <h1 className='text-xl text-[#0b111e] font-semibold'>Zadejte prosím své osobní údaje</h1>
                        <div>
                            <Label htmlFor="name" className="block mb-1">Vaše jméno a příjmení</Label>
                            <TextInput
                                id="name"
                                type="text"
                                sizing='lg'
                                placeholder="Jan Novák"
                                {...register('name', { required: 'Vaše jméno je povinné' })}
                                color={errors.name ? 'failure' : 'default'}
                                helperText={(
                                    <>
                                        {errors.name && (
                                            <p>
                                                {errors.name.message}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        <div>
                            <Label htmlFor="phone" className="block mb-1">Telefonní číslo</Label>
                            <PhoneInput
                                id="phone"
                                {...register('phone', {
                                    required: 'Telefonní číslo je povinné.',
                                    minLength: { value: 6, message: 'Zadejte platné telefonní číslo.' },
                                    validate: (phone) => isPossiblePhoneNumber(phone) || 'Zadejte platné telefonní číslo.'
                                })}
                                value={phone}
                                placeholder="Zadejte své telefonní číslo"
                                onChange={(phone) => setValue('phone', phone?.toString() || '')}
                                defaultCountry='CZ'
                                international
                                withCountryCallingCode
                                addInternationalOption={false}
                                countries={['CZ', 'SK']}
                                inputComponent={TextInput}
                            />
                            {errors.phone && (
                                <p className="text-sm text-red-500 mt-1">
                                    {errors.phone.message}
                                </p>
                            )}
                        </div>

                        <Button
                            type='submit'
                            size='lg'
                            color='primary'
                        >Pokračovat</Button>

                        <ErrorSwitch
                            error={saveUserDetails.error}
                            default={{ message: 'Při ukládání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                            options={[]}
                        />
                    </form>
                )}
            </div>
        </>
    );
};

export default PersonalDetailsView;
